(ns ui.view.grid-opensearch.columns-model
  "The pure model of the Edit columns modal of the grid. A column is a field path,
   as in the grid config. The draft is what the user edits in the modal. The draft
   is a map with `:visible` (the visible columns in display order), `:hidden` (the
   other columns) and `:locked` (a set of columns that are always visible). The
   functions here do not read the state of the app."
  (:require
   [common.opensearch.fields :as os-fields]
   [common.schema.registry :as registry]
   [common.util :as cu]))

(defn group-key
  "Return the group of a column. A field of the listed entity has the group
   `:root`. A field that comes through a reference has the first attribute of its
   path as group."
  [field-path]
  (if (and (sequential? field-path)
           (< 1 (count field-path)))
    (first field-path)
    :root))

(defn name-column-fn
  "Return a function of a column that is true when the column is a name column:
   its attribute is the name attribute of its entity. `tenant-schema` is the
   schema of the tenant. A column with no field schema has no attribute and no
   name attribute, so the function says true for it, as the grid does."
  [tenant-schema]
  (fn [column]
    (let [field-schema (registry/get-field-schema tenant-schema
                                                  (os-fields/field-path->last-kw column))]
      (= (:field/attribute field-schema)
         (:field/name-attribute field-schema)))))

(defn selected-name-column
  "Return the column that owns the row selection: the first column that is a name
   column, or the first column when no column is a name column. `name-column?` is
   a function of a column."
  [{:keys [columns name-column?]}]
  (or (first (filter name-column? columns))
      (first columns)))

(defn locked-columns
  "Return the set of the columns that are always visible: the mandatory columns
   and the column that owns the row selection."
  [{:keys [columns mandatory-columns name-column?]}]
  (let [selected (selected-name-column {:columns       columns
                                        :name-column? name-column?})]
    (cond-> (set mandatory-columns)
      (some? selected) (conj selected))))

(defn build-draft
  "Return the draft for the grid config. `columns` is the ordered `:columns`,
   `hidden-columns` is the set `:hidden-columns`, and `locked-columns` has the
   columns that are always visible. A locked column is visible and it comes first,
   also when the config hides it."
  [{:keys [columns hidden-columns locked-columns]}]
  (let [locked (set locked-columns)
        hidden-set (set hidden-columns)
        hidden? (fn [column]
                  (and (contains? hidden-set column)
                       (not (contains? locked column))))
        visible (vec (remove hidden? columns))]
    {:visible (vec (concat (filter locked visible)
                           (remove locked visible)))
     :hidden  (vec (filter hidden? columns))
     :locked  locked}))

(defn sections
  "Return the visible columns of the draft as sections. A section is a run of
   columns with the same group. A group that is not together in the draft has
   more than one section. A section is a map with `:group` and `:columns`."
  [draft]
  (->> (:visible draft)
       (partition-by group-key)
       (mapv (fn [section-columns]
               {:group   (group-key (first section-columns))
                :columns (vec section-columns)}))))

(defn locked?
  [draft column]
  (contains? (:locked draft) column))

(defn remove-column
  "Hide a column. The draft does not change for a locked column, or for a column
   that is not visible."
  [draft column]
  (if (or (locked? draft column)
          (not (some #{column} (:visible draft))))
    draft
    (-> draft
        (update :visible (fn [visible]
                           (vec (remove #{column} visible))))
        (update :hidden conj column))))

(defn add-column
  "Show a column at the end of the visible columns. The draft does not change for
   a column that is not hidden."
  [draft column]
  (if-not (some #{column} (:hidden draft))
    draft
    (-> draft
        (update :hidden (fn [hidden]
                          (vec (remove #{column} hidden))))
        (update :visible conj column))))

(defn move-in-section
  "Move a column inside one section of the draft. `section-index` is the index of
   the section, and `from` and `to` are indexes in that section. The draft does
   not change when an index is out of the section, or when a locked column is at
   one of the two indexes."
  [draft {:keys [section-index from to]}]
  (let [all-sections (sections draft)
        section (get all-sections section-index)
        section-columns (:columns section)
        offset (->> (take section-index all-sections)
                    (map (comp count :columns))
                    (reduce + 0))
        in-section? (fn [index]
                      (and (integer? index)
                           (<= 0 index)
                           (< index (count section-columns))))]
    (if (and section
             (in-section? from)
             (in-section? to)
             (not= from to)
             (not (locked? draft (get section-columns from)))
             (not (locked? draft (get section-columns to))))
      (update draft :visible cu/vec-move (+ offset from) (+ offset to))
      draft)))

(defn hidden-groups
  "Return the hidden columns of the draft in groups, for the picker of the
   columns to add. A group is a map with `:group` and `:columns`. The groups are
   in the order of their first column."
  [draft]
  (let [hidden (:hidden draft)
        by-group (group-by group-key hidden)]
    (mapv (fn [group]
            {:group   group
             :columns (vec (get by-group group))})
          (distinct (map group-key hidden)))))

(defn apply-draft
  "Return the grid config with the columns of the draft. The visible columns come
   first, in the order of the draft, and then the hidden columns. A column of the
   config that the draft does not know keeps its place after them, and keeps its
   hidden state. A locked column is never hidden."
  [{:keys [config draft]}]
  (let [draft-columns (distinct (concat (:visible draft) (:hidden draft)))
        known (set draft-columns)
        unknown-columns (remove known (:columns config))
        hidden-columns (into (set (remove (:locked draft) (:hidden draft)))
                             (remove known)
                             (:hidden-columns config))]
    (assoc config
           :columns (vec (concat draft-columns unknown-columns))
           :hidden-columns (set hidden-columns))))

(defn hidden-changed?
  "True when the two configs do not hide the same columns."
  [config next-config]
  (not= (set (:hidden-columns config))
        (set (:hidden-columns next-config))))
